import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimActKc2VersionOtherTotalItemData } from 'gql/graphql';
import { BimActKc2OtherTotalNameCellRenderer } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderOtherActsKc2/components/BimActKc2OtherTotalNameCellRenderer';
import { BimActKc2OtherTotalPriceCellRenderer } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderOtherActsKc2/components/BimActKc2OtherTotalPriceCellRenderer';
import { useBimActKC2OtherTotalInflation } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderOtherActsKc2/hooks/useBimActKC2OtherTotalInflation';

import type { ColDef, RowClassRules } from 'ag-grid-community';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { bimActKC2VersionOtherTotalsGql } from '../../workOrderVersionQueries';

interface IProps {
    actKC2Id: number;
}

const REQUEST_ID = 'BimWorkOrderOtherVersionActDetails';

export const useBimWorkOrderOtherVersionActDetailsData = ({ actKC2Id }: IProps) => {
    const dispatch = useDispatch();
    const { versionId } = useParams();

    const requestVersionVariables = useMemo(() => ({ actKC2VersionOtherId: actKC2Id }), [actKC2Id]);

    const [makeReset] = useResetStoreData({
        requestId: REQUEST_ID,
    });

    useEffect(
        () => () => {
            makeReset(undefined);
        },
        [makeReset],
    );

    const [data, status, , makeRequest] = useRequest({
        requestId: REQUEST_ID,
        query: bimActKC2VersionOtherTotalsGql,
        variables: requestVersionVariables,
        withEffect: !!actKC2Id && !!versionId,
    });

    const bimActKC2OtherTotalsMakeRequest = useCallback(() => {
        makeReset(undefined);
        dispatch(makeRequest(requestVersionVariables));
    }, [dispatch, makeRequest, makeReset, requestVersionVariables]);

    const { bimUseActKC2OtherTotalInflationStatus, bimUseActKC2OtherTotalInflationRequest } =
        useBimActKC2OtherTotalInflation(actKC2Id, bimActKC2OtherTotalsMakeRequest);

    const isLoadingVersion = useMemo(
        () => status === RequestStatus.FETCHING || bimUseActKC2OtherTotalInflationStatus === RequestStatus.FETCHING,
        [bimUseActKC2OtherTotalInflationStatus, status],
    );

    const detailsVersionColumns = useMemo<ColDef<BimActKc2VersionOtherTotalItemData>[]>(
        () => [
            {
                headerName: 'Наименование работ, затрат',
                field: 'name',
                flex: 3,
                cellRenderer: BimActKc2OtherTotalNameCellRenderer,
                cellRendererParams: {
                    bimUseActKC2OtherTotalInflationRequest,
                    isVersion: !!versionId,
                },
            },
            {
                headerName: 'Всего',
                field: 'total',
                flex: 1,
                cellRenderer: BimActKc2OtherTotalPriceCellRenderer,
                cellClass: 'ag-right-aligned-cell',
            },
        ],
        [bimUseActKC2OtherTotalInflationRequest, versionId],
    );

    const rowClassVersionRules = useMemo<RowClassRules>(
        () => ({
            boldWeight: ({ data: rowData }: { data: BimActKc2VersionOtherTotalItemData }) =>
                rowData?.sortOrder !== 3 && !rowData?.isInflation,
            totalsRow: ({ data: rowData }: { data: BimActKc2VersionOtherTotalItemData }) =>
                rowData?.sortOrder === data?.bimActKC2VersionOtherTotals?.items?.length,
        }),
        [data?.bimActKC2VersionOtherTotals?.items?.length],
    );

    return {
        detailsVersionData: data?.bimActKC2VersionOtherTotals?.items,
        detailsVersionColumns,
        isLoadingVersion,
        rowClassVersionRules,
    };
};
